import createClient, { type FetchOptions } from "openapi-fetch"
import type { FilterKeys, PathsWithMethod } from "openapi-typescript-helpers"
import {
	forceSignOut,
	getFreshAccessToken,
	refreshSessionOnce,
} from "@/lib/auth"
import type { components, paths } from "./schema"

export type Schema = components["schemas"]

type GetPath = PathsWithMethod<paths, "get">
type PostPath = PathsWithMethod<paths, "post">
type PutPath = PathsWithMethod<paths, "put">
type PatchPath = PathsWithMethod<paths, "patch">
type DeletePath = PathsWithMethod<paths, "delete">

export const API_BASE_URL =
	import.meta.env.VITE_API_URL || "http://localhost:8000"

let lastRequestId: string | null = null

export function getLastRequestId(): string | null {
	return lastRequestId
}

async function applyAuthHeaders(headers: Headers) {
	const token = await getFreshAccessToken()
	if (token) headers.set("Authorization", `Bearer ${token}`)
	const requestId = crypto.randomUUID()
	headers.set("X-Request-ID", requestId)
	lastRequestId = requestId
}

export async function authedFetch(request: Request): Promise<Response> {
	await applyAuthHeaders(request.headers)
	const retryable = request.clone()
	let response = await fetch(request)
	if (response.status !== 401) return response

	if (await refreshSessionOnce()) {
		await applyAuthHeaders(retryable.headers)
		response = await fetch(retryable)
		if (response.status !== 401) return response
	}

	await forceSignOut()
	return response
}

export const api = createClient<paths>({
	baseUrl: API_BASE_URL,
	fetch: authedFetch,
	querySerializer: { array: { style: "form", explode: true } },
})

const HTTP_METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"] as const
type HttpMethod = (typeof HTTP_METHODS)[number]

function hasStringField<K extends string>(
	body: unknown,
	key: K,
): body is Record<K, string> {
	return (
		typeof body === "object" &&
		body !== null &&
		key in body &&
		typeof (body as Record<string, unknown>)[key] === "string"
	)
}

export function getApiErrorDetail(error: unknown, fallback: string): string {
	if (!(error instanceof ApiError)) return fallback
	if (hasStringField(error.body, "detail")) return error.body.detail
	if (hasStringField(error.body, "message")) return error.body.message
	return fallback
}

function getApiErrorMessage(body: unknown, status: number) {
	if (hasStringField(body, "detail")) return body.detail
	if (hasStringField(body, "message")) return body.message
	if (typeof body === "string" && body) return body
	return `status ${status}`
}

async function readErrorBody(response: Response) {
	const text = await response.text().catch(() => "")
	if (!text) return undefined
	try {
		return JSON.parse(text)
	} catch {
		return text
	}
}

function toHttpMethod(method: string | undefined): HttpMethod {
	const candidate = method?.toUpperCase() ?? "GET"
	return (HTTP_METHODS as readonly string[]).includes(candidate)
		? (candidate as HttpMethod)
		: "GET"
}

export class ApiError extends Error {
	readonly status: number
	readonly path: string
	readonly method: HttpMethod
	readonly requestId: string | null
	readonly body: unknown

	constructor(
		method: HttpMethod,
		path: string,
		body: unknown,
		response: Response | undefined,
	) {
		const status = response?.status ?? 0
		const message = getApiErrorMessage(body, status)
		super(`${method} ${path} failed: ${message}`)
		this.name = "ApiError"
		this.status = status
		this.path = path
		this.method = method
		this.requestId = response?.headers.get("x-request-id") ?? null
		this.body = body
	}
}

export async function apiFetch(input: string | URL, init?: RequestInit) {
	const response = await authedFetch(new Request(input, init))
	if (!response.ok) {
		throw new ApiError(
			toHttpMethod(init?.method),
			input.toString(),
			await readErrorBody(response),
			response,
		)
	}
	return response
}

export async function GET<P extends GetPath>(
	path: P,
	options?: FetchOptions<FilterKeys<paths[P], "get">>,
) {
	// biome-ignore lint/suspicious/noExplicitAny: internal type coercion for optional params
	const { data, error, response } = await api.GET(path, options as any)
	if (error) throw new ApiError("GET", path, error, response)
	return data
}

export async function POST<P extends PostPath>(
	path: P,
	options?: FetchOptions<FilterKeys<paths[P], "post">>,
) {
	// biome-ignore lint/suspicious/noExplicitAny: internal type coercion for optional params
	const { data, error, response } = await api.POST(path, options as any)
	if (error) throw new ApiError("POST", path, error, response)
	return data
}

export async function PUT<P extends PutPath>(
	path: P,
	options?: FetchOptions<FilterKeys<paths[P], "put">>,
) {
	// biome-ignore lint/suspicious/noExplicitAny: internal type coercion for optional params
	const { data, error, response } = await api.PUT(path, options as any)
	if (error) throw new ApiError("PUT", path, error, response)
	return data
}

export async function PATCH<P extends PatchPath>(
	path: P,
	options?: FetchOptions<FilterKeys<paths[P], "patch">>,
) {
	// biome-ignore lint/suspicious/noExplicitAny: internal type coercion for optional params
	const { data, error, response } = await api.PATCH(path, options as any)
	if (error) throw new ApiError("PATCH", path, error, response)
	return data
}

export async function DELETE<P extends DeletePath>(
	path: P,
	options?: FetchOptions<FilterKeys<paths[P], "delete">>,
) {
	// biome-ignore lint/suspicious/noExplicitAny: internal type coercion for optional params
	const { data, error, response } = await api.DELETE(path, options as any)
	if (error) throw new ApiError("DELETE", path, error, response)
	return data
}
